import React, { useEffect, useState } from "react";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";
import { CheckCircle2, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { useOps } from "@/lib/opsCards";
import { BUILTIN_ACTIONS } from "@/lib/builtinForms";
import FormRenderer, { initialValues } from "./FormRenderer";
import RecordsTable from "./RecordsTable";
import DocLibrary from "./DocLibrary";
import MessageCenter from "./MessageCenter";

function Recent({ form, refreshKey, onChanged }) {
  const { activity } = useOps();
  const action = form.kind === "builtin" ? BUILTIN_ACTIONS[form.action] : null;
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (action?.recent) action.recent.list().then(setItems).catch(() => setItems([]));
    else setItems(activity.filter((a) => a.form_id === form.id).slice(0, 5).map((a) => ({ id: a.id, at: a.at, text: a.summary })));
  }, [form.id, refreshKey, activity]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;
  const undo = async (id) => {
    if (!window.confirm("Undo this entry?")) return;
    await action.recent.remove(id); toast.success("Entry removed"); onChanged();
  };
  return (
    <div data-testid="ops-quick-recent" className="mt-5 pt-4 border-t border-slate-800">
      <div className="font-mono text-[0.55rem] tracking-[0.2em] uppercase text-slate-500 mb-2">Recent</div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-2 text-xs text-slate-300">
            <span className="text-slate-500 font-mono shrink-0">{new Date(it.at).toLocaleDateString()}</span>
            <span className="truncate flex-1">{it.text}</span>
            {action?.recent && (
              <button type="button" onClick={() => undo(it.id)} data-testid={`ops-quick-undo-${it.id}`} aria-label="Undo"
                className="p-1 rounded text-slate-500 hover:text-amber-300 transition-colors"><Undo2 className="w-3 h-3" /></button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickForm({ form }) {
  const { reload } = useOps();
  const [values, setValues] = useState(() => initialValues(form.fields));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const prefix = `ops-quick-${form.id.replace(":", "-")}`;
  const changed = () => { setRefreshKey((k) => k + 1); reload(); };

  const submit = async (e) => {
    e.preventDefault();
    const missing = form.fields.find((f) => f.required && !f.archived && (values[f.key] === "" || values[f.key] == null));
    if (missing) return toast.error(`${missing.label} is required`);
    const action = form.kind === "builtin" ? BUILTIN_ACTIONS[form.action] : null;
    const err = action?.validate?.(values);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      if (action) {
        await action.submit(values);
        apiClient.post("/admin/ops/activity", { form_id: form.id, summary: action.summary(values) }).catch(() => {});
      } else {
        await apiClient.post(`/admin/ops/forms/${form.id}/records`, { values });
      }
      setDone(true); setTimeout(() => setDone(false), 1800);
      setValues(initialValues(form.fields));
      changed();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid={`${prefix}-form`}>
      <FormRenderer form={form} values={values} setValues={setValues} prefix={prefix} />
      <button type="submit" disabled={busy} data-testid={`${prefix}-submit`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.65rem] font-bold tracking-[0.16em] uppercase hover:brightness-110 active:scale-[0.99] transition-[filter,transform] disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
        {done ? "Saved" : "Save"}
      </button>
      <Recent form={form} refreshKey={refreshKey} onChanged={changed} />
    </form>
  );
}

// Popup body for a configured card: each attached form is a tab; custom forms add a Records tab.
export default function QuickFormsContent({ win }) {
  const { formById } = useOps();
  const forms = (win.payload?.forms || []).map(formById).filter(Boolean);
  const [view, setView] = useState("entry");
  const ct = useCardTabs(forms[0]?.id || "", () => setView("entry"));
  const tab = ct.tab;
  const setTab = ct.open;
  const active = forms.find((f) => f.id === tab) || forms[0];
  if (!active) return <p className="text-sm text-slate-400">No forms attached to this card yet.</p>;
  if (ct.deck && forms.length > 1) return <div ref={ct.ref} data-testid="ops-quick-forms"><CardDeck testid="ops-quick-cards" items={forms.map((f) => ({ id: f.id, label: f.name, desc: f.description || (f.kind === "custom" ? "New entry or past records" : "Fill in and submit") }))} onOpen={setTab} /></div>;
  return (
    <div ref={ct.ref} data-testid="ops-quick-forms">
      {ct.compact && forms.length > 1 && <CardsBack testid="ops-quick-cards-back" label={active.name} onClick={ct.close} />}
      {forms.length > 1 && !ct.compact && (
        <div className="flex flex-wrap gap-1 mb-4" role="tablist">
          {forms.map((f) => (
            <button key={f.id} type="button" onClick={() => { setTab(f.id); setView("entry"); }} data-testid={`ops-quick-tab-${f.id.replace(":", "-")}`}
              className={`px-3 py-1.5 rounded-md font-mono text-[0.6rem] tracking-[0.14em] uppercase border transition-colors ${active.id === f.id ? "border-brand-cyan/60 bg-brand-cyan/10 text-brand-cyan" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
              {f.name}
            </button>
          ))}
        </div>
      )}
      {active.kind === "custom" && (
        <div className="flex gap-3 mb-3 font-mono text-[0.6rem] tracking-[0.14em] uppercase">
          {["entry", "records"].map((v) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`ops-quick-view-${v}`}
              className={view === v ? "text-brand-cyan border-b border-brand-cyan pb-0.5" : "text-slate-500 hover:text-slate-300"}>{v === "entry" ? "New entry" : "Records"}</button>
          ))}
        </div>
      )}
      {active.action === "documents" ? <DocLibrary docs={win.payload?.docs} />
        : active.action === "messages" ? <MessageCenter />
        : view === "records" && active.kind === "custom"
        ? <RecordsTable formId={active.id} compact />
        : <QuickForm key={active.id} form={active} />}
    </div>
  );
}
